import { Button, ButtonProps as BaseButtonProps, extendVariants, useButton, VariantProps } from "@nextui-org/react";
import { forwardRef } from "react";

const PRIMARY_GRADIENT_COLOR_CLASSES = "from-violet-500 from-[10%] to-blue-700 text-white";

const ExtendedButton = extendVariants(Button, {
  variants: {
    default: {
      custom: "px-5 cursor-pointer leading-[0]",
    },

    variant: {
      primary: "text-[16px] font-normal rounded-[12px]",
      primary_large: "text-[14px] font-medium !w-[192px] min-w-min w-min rounded-[12px]",
      primary_large_tall: "text-[14px] font-medium !w-[192px] min-w-min w-min h-[44px] leading-3 rounded-[12px]",
      primary_large_bordered: "text-[14px] font-medium !w-[192px] min-w-min w-min rounded-[12px] border-1 border-solid border-foreground",
      primary_small_short: "text-[14px] font-medium w-min min-w-min h-[24px] rounded-md px-[10px]",
      primary_smallest: "text-[14px] font-medium w-min min-w-min rounded-md px-0 h-min",

      secondary: "text-[14px] font-medium rounded-[12px]",
      secondary_large: "text-[14px] font-medium min-w-[192px] rounded-[12px]",
      secondary_short: "text-[12px] font-medium px-5 h-[24px] rounded-[7px] px-1.5",
      secondary_small: "text-[14px] font-medium min-w-[50px] rounded-[12px]",
      secondary_large_short: "text-[14px] font-medium min-w-[150px] max-w-full h-[32px] rounded-[8px]",
      secondary_bordered: "text-[14px] font-medium !rounded-[9px] border-1 border-solid border-blue-400 !text-black dark:!text-white px-2.5",

      tertiary_small: "text-[14px] font-medium min-w-[40px] rounded-[12px] px-0",
      tertiary_small_short: "text-[14px] font-medium min-w-[40px] h-[20px] rounded-[12px]",
      tertiary_short: "font-medium px-5 h-[32px] rounded-[7px] w-min px-2.5",

      bordered: "border-1 border-solid border-border",

      giveaway:
        "bg-transparent h-[28px] px-4 rounded-lg cursor-pointer max-sm:px-2 bg-gradient-to-r from-purple-600 to-pink-600 text-[16px] pb-0.5 text-white",
    },

    color: {
      background: "bg-white dark:bg-black",
      primary_light: "bg-primaryLight data-[hover=true]:bg-primaryLightHover text-primaryLightContrast",
      primary_medium: "bg-blue-600 data-[hover=true]:bg-blue-600 text-white bg-opacity-50",
      primary_strong: "bg-primaryStrong text-white",
      danger_light: "bg-dangerLight text-dangerLightContrast",
      secondary_strong: "bg-purple-600 dark:bg-purple-700 text-white",
      transparent: "bg-transparent text-foreground",
      transparent_bordered: "bg-transparent border-1 border-solid border-foreground",
      transparent_bordered_light: "bg-transparent border-1 border-solid border-white",
      disabled: "bg-gray-400 dark:bg-gray-500 text-black dark:text-gray-900",
      gray_light: "text-black dark:text-white",
      warning_strong: "bg-yellow-600 text-white",
      moderation: "bg-colorModeration",
      under_review: "text-black bg-colorUnderReview",
      fix_required: "text-black bg-colorFixRequired",
      neutral: "bg-neutral",
      orange: "bg-orange text-white",
      gradient: "bg-gradient-to-r from-cyan-600 from-[-63.59%] via-pink-600 via-[-20.3%] to-blue-600 to-[70.46%] text-white",
      gradient_primary: `bg-gradient-to-r ${PRIMARY_GRADIENT_COLOR_CLASSES}`,
      gradient_primary_alt: `bg-gradient-to-l ${PRIMARY_GRADIENT_COLOR_CLASSES}`,
      gradient_secondary: "bg-gradient-to-b from-[#1E90FF] from-[10%] to-[#9042DD] text-white",
      gradient_tertiary: "bg-gradient-to-b from-[#979797] to-[#6e6f71] to-[70%] text-white",
      pink_light: "bg-pink-1000 text-white",
      error: "bg-red-600 text-white",
      error_strong: "bg-[rgb(224,17,54)] text-white",
      error_light: "bg-[rgba(252,102,102,1)] text-white",
      success: "bg-success text-white",
      gray: "bg-gray-300 dark:bg-gray-50 !text-gray-800 border-none",
      manual_review_required: "bg-red-8 ![&.color]-white",
    },

    isIconOnly: {
      true: "px-0 bg-transparent data-[hover=true]:bg-transparent text-black dark:text-white",
    },
    isDisabled: {
      true: "bg-gray-600 dark:bg-gray-500 text-black dark:text-gray-900",
    },
    radius: {
      full: "!rounded-full",
    },
  },
  defaultVariants: {
    default: "custom",
  },
  compoundVariants: [
    {
      isDisabled: true,
      isIconOnly: undefined,
      class: "dark:!bg-[#666666] dark:opacity-[100]",
    },
    {
      isDisabled: true,
      isIconOnly: true,
      class: "!bg-transparent",
    },
    {
      isDisabled: true,
      color: "error_light",
      class: "!bg-[rgba(252,102,102,1)] !text-white",
    },
  ],
});

type ExtendedButtonVariants = VariantProps<typeof ExtendedButton>;

interface ButtonBaseProps extends Omit<BaseButtonProps, "color" | "variant">, Partial<ExtendedButtonVariants> {
  to?: string;
}

const SpicyButton = forwardRef<HTMLButtonElement, ButtonBaseProps>((props, ref) => {
  const { variant: _variant, color: _color, ...restProps } = props;
  const { domRef } = useButton({ ref, ...restProps });

  const { startContent, endContent, children, ...otherProps } = props;

  return (
    <ExtendedButton ref={domRef} {...otherProps}>
      {startContent}
      <span className="flex items-center justify-center text-center gap-1.5">{children}</span>
      {endContent}
    </ExtendedButton>
  );
});

SpicyButton.displayName = "SpicyButton";

export default SpicyButton;
